import React, { useState, useEffect } from 'react';
import './PricingPage.css';

// Data-driven plan definitions
const plans = [
  {
    id: 'basic',
    name: 'Basic',
    monthly: 29,
    annual: 24,
    features: {
      'Unlimited calls (US)': true,
      'Up to users': 'Up to 70 users',
      'SMS support': true,
      'Unlimited Email to Fax': true,
      'Unlimited Fax to Email': true,
      'Unlimited Audio Conferencing': true,
      'Quality of Service Reports': true,
      'Call Log Reports': true,
      'Call Queue': true,
      'Auto Attendants': true,
      'Time based rules for call Routing': true,
      'IVR': true,
      'Hunt Groups': true,
      'Voicemail to Email': true,
      'Basic support': true,
    },
  },
  {
    id: 'premium',
    name: 'Premium',
    monthly: 39,
    annual: 32,
    features: {
      'Unlimited calls (US)': true,
      'Up to users': 'Up to 200 users',
      'SMS support': true,
      'Unlimited Email to Fax': true,
      'Unlimited Fax to Email': true,
      'Unlimited Audio Conferencing': true,
      'Quality of Service Reports': true,
      'Call Log Reports': true,
      'Call Recording': true,
      'Call Queue': true,
      'Auto Attendants': true,
      'Time based rules for call Routing': true,
      'IVR': true,
      'Hunt Groups': true,
      'Voicemail transcription': true,
      'Voicemail to Email': true,
      'Includes VoIP Phone': true,
      'Priority support': true,
    },
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    monthly: null,
    annual: null,
    features: {
      'Unlimited calls (US)': true,
      'Up to users': 'Unlimited users',
      'SMS support': true,
      'Unlimited Email to Fax': true,
      'Unlimited Fax to Email': true,
      'Unlimited Audio Conferencing': true,
      'Quality of Service Reports': true,
      'Call Log Reports': true,
      'Call Recording': true,
      'Call Queue': true,
      'Auto Attendants': true,
      'Time based rules for call Routing': true,
      'IVR': true,
      'Hunt Groups': true,
      'Voicemail transcription': true,
      'Voicemail to Email': true,
      'Includes VoIP Phone': true,
      'Single Sign On': true,
      'Custom API Integrations': true,
      'Dedicated 24/7 support': true,
    },
  },
];

// Ordered list of features
const featureLabels = [
  'Unlimited calls (US)',
  'Up to users',
  'SMS support',
  'Unlimited Email to Fax',
  'Unlimited Fax to Email',
  'Unlimited Audio Conferencing',
  'Quality of Service Reports',
  'Call Log Reports',
  'Call Recording',
  'Call Queue',
  'Auto Attendants',
  'Time based rules for call Routing',
  'IVR',
  'Hunt Groups',
  'Voicemail transcription',
  'Voicemail to Email',
  'Includes VoIP Phone',
  'Basic support',
  'Priority support',
  'Single Sign On',
  'Custom API Integrations',
  'Dedicated 24/7 support',
];
// Grouped enterprise-grade FAQ sections
const faqSections = [
  {
    title: 'General', items: [
      { q: 'Is there a free trial?', a: 'Yes—enjoy a 30-day free trial with no credit card required.' },
      { q: 'How can I cancel?', a: 'You can cancel anytime in your account settings; there are no hidden fees.' },
      { q: 'Do you offer multi-year discounts?', a: 'Yes—contact us for pricing on annual or multi-year commitments.' },
    ],
  },
  {
    title: 'Plans & Billing', items: [
      { q: 'How are overages handled?', a: 'Any usage beyond plan limits is automatically billed at our published overage rates.' },
      { q: 'Is there a setup fee?', a: 'No—there are no upfront setup fees for any plan.' },
      { q: 'What payment methods are accepted?', a: 'We accept all major credit cards, ACH, wire transfers, and POs for enterprise accounts.' },
    ],
  },
  {
    title: 'Security & Compliance', items: [
      { q: 'What security certifications do you have?', a: 'We are ISO 27001 certified and SOC 2 Type II compliant.' },
      { q: 'Is data encrypted at rest and in transit?', a: 'Yes—we use AES‑256 encryption for data at rest and TLS 1.2+ for data in transit.' },
      { q: 'How do you handle disaster recovery?', a: 'Our platform is deployed across multiple regions with automatic failover and backups.' },
    ],
  },
  {
    title: 'Integrations & API', items: [
      { q: 'What CRM integrations are available?', a: 'We offer connectors for Salesforce, HubSpot, Zendesk, and ServiceNow.' },
      { q: 'Is there a public API?', a: 'Yes—our RESTful API lets you manage users, calls, recordings, and reports.' },
      { q: 'Can I build custom connectors?', a: 'Absolutely—contact us to scope your custom integration requirements.' },
    ],
  },
  {
    title: 'Support & SLA', items: [
      { q: 'What uptime SLA do you offer?', a: 'We guarantee 99.99% uptime backed by our SLA with service credits.' },
      { q: 'What support levels are available?', a: 'Standard 24×5 support included; upgrade to 24×7 and a TAM.' },
      { q: 'How do I submit a support ticket?', a: 'Use our web portal, email support@voiceproconnect.com, or call our support line.' },
    ],
  },
];

// Optional Add‑Ons & Modules
// const addons = [
//   { id: 'local-numbers', name: 'Local Phone Numbers', description: 'Add US local DIDs', price: '$1.00/number/mo' },
//   { id: 'tollfree-numbers', name: 'Toll‑Free Numbers', description: 'Toll‑free DID', price: '$2.50/number/mo' },
//   { id: 'sms-numbers', name: 'SMS‑Enabled Numbers', description: 'Enable SMS/MMS on your DIDs', price: '$0.05/SMS' },
//   { id: 'ai-agent-seats', name: 'AI Agent Seats', description: 'Per‑seat fee for AI Agent conversations', price: '$49/user/mo' },
//   { id: 'transcription', name: 'Call Transcription', description: 'Speech‑to‑Text transcription', price: '$0.01/minute' },
//   { id: 'api-integration', name: 'Custom API Integration Hours', description: 'Professional services', price: '$200/hour' },
//   { id: 'compliance-pack', name: 'Compliance Pack', description: 'HIPAA & PCI compliance', price: '$500/month' },
// ];
const addons = [
  {
    id: 'local-numbers',
    name: 'Local Phone Numbers',
    description: 'Provision local US phone numbers so your customers can dial in on familiar, domestic lines.',
    price: <a href="/login" aria-label="Login to see pricing">→</a>,
  },
  {
    id: 'tollfree-numbers',
    name: 'Toll-Free Numbers',
    description: 'Provide your business with toll-free DIDs (800/888/etc.) to give callers a complimentary dialing experience.',
    price: <a href="/login" aria-label="Login to see pricing">→</a>,
  },
  {
    id: 'sms-numbers',
    name: 'SMS-Enabled Numbers',
    description: 'Enable SMS/MMS capabilities on your existing phone numbers for two-way text messaging.',
    price: <a href="/login" aria-label="Login to see pricing">→</a>,
  },
  {
    id: 'ai-agent-seats',
    name: 'AI Agent Seats',
    description: 'Unlock conversational AI seats for your team so each user can run unlimited voice-agent sessions.',
    price: <a href="/login" aria-label="Login to see pricing">→</a>,
  },
  {
    id: 'ai-receptionist',
    name: 'AI Receptionist',
    description: 'Automate call greeting, filtering, and routing with our intelligent virtual receptionist.',
    price: <a href="/login" aria-label="Login to see pricing">→</a>,
  },
  {
    id: 'transcription',
    name: 'Call Transcription',
    description: 'Capture every word with real-time speech-to-text transcription for compliance and analytics.',
    price: <a href="/login" aria-label="Login to see pricing">→</a>,
  },
  {
    id: 'recording-archive',
    name: 'Extended Recording Archive',
    description: 'Store and retrieve call recordings securely for up to 12 months.',
    price: <a href="/login" aria-label="Login to see pricing">→</a>,
  },
  {
    id: 'api-integration',
    name: 'Custom API Integration Hours',
    description: 'Leverage our professional services team for bespoke API integrations and custom workflows.',
    price: <a href="/login" aria-label="Login to see pricing">→</a>,
  },
  {
    id: 'compliance-pack',
    name: 'Compliance Pack',
    description: 'Ensure HIPAA & PCI compliance with our full suite of security measures and audits.',
    price: <a href="/login" aria-label="Login to see pricing">→</a>,
  },
];

export default function PricingPage() {
  const [annual, setAnnual] = useState(false);
  const [showEnterpriseForm, setShowEnterpriseForm] = useState(false);
  const [openFaqKey, setOpenFaqKey] = useState(null);

  // SEO: update title & meta description
  useEffect(() => {
    document.title = 'Pricing | VoicePro Communications';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        'content',
        'Compare our Basic, Premium and Enterprise plans for secure, reliable VoIP & fax services.'
      );
    }
  }, []);

  const discount = annual ? Math.round((1 - plans[0].annual / plans[0].monthly) * 100) : 0;
  // Enterprise wizard state
  const [enterpriseStep, setEnterpriseStep] = useState(1);
  const stepTitles = ['Company Details', 'Your Requirements', 'Contact & Schedule', 'Review & Submit'];
  const [enterpriseData, setEnterpriseData] = useState({
    companyName: '', website: '', industry: '', locations: '',
    estimatedUsers: '', useCases: [], integrations: '', complianceNeeds: [],
    contactName: '', contactTitle: '', contactEmail: '', contactPhone: '',
    timeZone: '', preferredContact: '', dateTime: '', agreed: false,
  });
  const nextStep = () => setEnterpriseStep(s => Math.min(s + 1, 5));
  const prevStep = () => setEnterpriseStep(s => Math.max(s - 1, 1));
  const handleChange = (field) => (e) => setEnterpriseData(d => ({ ...d, [field]: e.target.value }));
  const handleCheckbox = (field, value) => {
    setEnterpriseData(d => {
      const exists = d[field].includes(value);
      const arr = exists ? d[field].filter(v => v !== value) : [...d[field], value];
      return { ...d, [field]: arr };
    });
  };
  const handleAgree = () => setEnterpriseData(d => ({ ...d, agreed: !d.agreed }));
  const handleSubmitWizard = (e) => { e.preventDefault(); setEnterpriseStep(5); };

  return (
    <div className="pricing-page">
      <section className="pricing-hero">
        <h1>Simple, predictable per-user pricing</h1>
        <p>
          All the calling, reporting and AI Solutions you need—all backed by 24/7 enterprise support.
        </p>
      </section>
      <div className="trust-signals">
        <i className="fa fa-users" aria-hidden="true"></i> Trusted by 500+ businesses&nbsp;|
        &nbsp;<i className="fa fa-shield" aria-hidden="true"></i> 30-day money-back guarantee&nbsp;|
        &nbsp;<i className="fa fa-lock" aria-hidden="true"></i> GDPR & ISO 27001 compliant
      </div>
      <div className="pricing-toggle">
        <span>Monthly</span>
        <label className="switch">
          <input
            type="checkbox"
            checked={annual}
            onChange={() => setAnnual(!annual)}
            aria-label="Toggle annual pricing"
          />
          <span className="slider round"></span>
        </label>
        <span>Annually (save {discount}%)</span>
      </div>
      <table className="pricing-table">
        <caption className="sr-only">Pricing comparison table</caption>
        <thead>
          <tr>
            <th scope="col">Features</th>
            {plans.map(plan => (
              <th
                key={plan.id}
                scope="col"
                className={plan.id === 'premium' ? 'most-popular' : ''}
              >
                {plan.name}
                <div className="price">
                  {plan.monthly
                    ? `$${annual ? plan.annual : plan.monthly}/user/mo`
                    : 'Call Sales'}
                </div>
                {plan.id !== 'enterprise' ? (
                  <button
                    className="buy-button"
                    onClick={() => window.location.href = `/buy-now?plan=${plan.id}`}
                  >
                    Buy Now
                  </button>
                ) : (
                  <button
                    className="buy-button"
                    onClick={() => setShowEnterpriseForm(true)}
                  >
                    Contact Us
                  </button>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {featureLabels.map(label => (
            <tr key={label}>
              <td>{label}</td>
              {plans.map(plan => (
                <td key={plan.id}>
                  {plan.features[label]
                    ? typeof plan.features[label] === 'string'
                      ? plan.features[label]
                      : <span className="checkmark" aria-label="Included">✓</span>
                    : null}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <section className="addons-section">
        <h2>Optional Add‑Ons & Modules</h2>
        <div className="addons-section-main">
        <div className="addons-grid">
          {addons.map(a => (
            <div key={a.id} className="addon-card">
              <h3>{a.name}</h3>
              <p>{a.description}</p>
              <div className="addon-price">{a.price}</div>
            </div>
          ))}
        </div>
        </div>
      </section>
      {showEnterpriseForm && (
        <div className="modal-overlay" onClick={() => setShowEnterpriseForm(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <button className="close-modal" onClick={() => setShowEnterpriseForm(false)} aria-label="Close form">×</button>
            {enterpriseStep <= 4 && (
              <div className="wizard-stepper">
                <span>Step {enterpriseStep} of 4: {stepTitles[enterpriseStep - 1]}</span>
              </div>
            )}
            <form onSubmit={enterpriseStep === 4 ? handleSubmitWizard : e => { e.preventDefault(); nextStep(); }}>
              {enterpriseStep === 1 && (
                <div className="wizard-step">
                  <h3>Company Details</h3>
                  <input type="text" placeholder="Company Name" value={enterpriseData.companyName} onChange={handleChange('companyName')} required />
                  <input type="url" placeholder="Website" value={enterpriseData.website} onChange={handleChange('website')} />
                  <select value={enterpriseData.industry} onChange={handleChange('industry')} required>
                    <option value="">Select Industry</option>
                    <option>Telecom</option>
                    <option>Healthcare</option>
                    <option>Finance</option>
                    <option>Education</option>
                    <option>Other</option>
                  </select>
                  <input type="number" placeholder="Number of Locations" value={enterpriseData.locations} onChange={handleChange('locations')} />
                </div>
              )}
              {enterpriseStep === 2 && (
                <div className="wizard-step">
                  <h3>Your Requirements</h3>
                  <input type="number" placeholder="Estimated # of Users" value={enterpriseData.estimatedUsers} onChange={handleChange('estimatedUsers')} required />
                  <div className="wizard-checkbox-group">
                    <label><input type="checkbox" checked={enterpriseData.useCases.includes('Calling')} onChange={() => handleCheckbox('useCases','Calling')} /> Calling</label>
                    <label><input type="checkbox" checked={enterpriseData.useCases.includes('Fax')} onChange={() => handleCheckbox('useCases','Fax')} /> Fax</label>
                    <label><input type="checkbox" checked={enterpriseData.useCases.includes('Conferencing')} onChange={() => handleCheckbox('useCases','Conferencing')} /> Conferencing</label>
                    <label><input type="checkbox" checked={enterpriseData.useCases.includes('API')} onChange={() => handleCheckbox('useCases','API')} /> API</label>
                    <label><input type="checkbox" checked={enterpriseData.useCases.includes('SSO')} onChange={() => handleCheckbox('useCases','SSO')} /> SSO</label>
                  </div>
                  <input type="text" placeholder="Integrations (CRM, ERP...)" value={enterpriseData.integrations} onChange={handleChange('integrations')} />
                  <div className="wizard-checkbox-group">
                    <label><input type="checkbox" checked={enterpriseData.complianceNeeds.includes('GDPR')} onChange={() => handleCheckbox('complianceNeeds','GDPR')} /> GDPR</label>
                    <label><input type="checkbox" checked={enterpriseData.complianceNeeds.includes('HIPAA')} onChange={() => handleCheckbox('complianceNeeds','HIPAA')} /> HIPAA</label>
                    <label><input type="checkbox" checked={enterpriseData.complianceNeeds.includes('ISO 27001')} onChange={() => handleCheckbox('complianceNeeds','ISO 27001')} /> ISO 27001</label>
                  </div>
                </div>
              )}
              {enterpriseStep === 3 && (
                <div className="wizard-step">
                  <h3>Contact & Schedule</h3>
                  <input type="text" placeholder="Your Name" value={enterpriseData.contactName} onChange={handleChange('contactName')} required />
                  <input type="text" placeholder="Your Title" value={enterpriseData.contactTitle} onChange={handleChange('contactTitle')} />
                  <input type="email" placeholder="Work Email" value={enterpriseData.contactEmail} onChange={handleChange('contactEmail')} required />
                  <input type="tel" placeholder="Phone" value={enterpriseData.contactPhone} onChange={handleChange('contactPhone')} />
                  <input type="text" placeholder="Time Zone" value={enterpriseData.timeZone} onChange={handleChange('timeZone')} />
                  <select value={enterpriseData.preferredContact} onChange={handleChange('preferredContact')}>
                    <option value="">Preferred Follow-up Method</option>
                    <option>Phone</option>
                    <option>Video Call</option>
                    <option>Email</option>
                  </select>
                  <input type="datetime-local" value={enterpriseData.dateTime} onChange={handleChange('dateTime')} />
                </div>
              )}
              {enterpriseStep === 4 && (
                <div className="wizard-step">
                  <h3>Review & Submit</h3>
                  <ul className="review-list">
                    {Object.entries(enterpriseData).map(([key,val]) => (
                      key !== 'agreed' && <li key={key}><strong>{key.replace(/([A-Z])/g,' $1')}:</strong> {Array.isArray(val) ? val.join(', ') : val}</li>
                    ))}
                  </ul>
                  <label className="wizard-checkbox-group">
                    <input type="checkbox" checked={enterpriseData.agreed} onChange={handleAgree} /> I agree to the <a href="/terms" target="_blank">Terms of Service</a>
                  </label>
                </div>
              )}
              {enterpriseStep === 5 && (
                <div className="wizard-step">
                  <h3>Thank you!</h3>
                  <p>Your request is on its way to our Sales team. We’ll reach out within 1 business day.</p>
                  <button type="button" onClick={() => setShowEnterpriseForm(false)}>Close</button>
                </div>
              )}
              {enterpriseStep <= 4 && (
                <div className="wizard-actions">
                  {enterpriseStep > 1 && <button type="button" onClick={prevStep}>Back</button>}
                  <button type="submit" disabled={enterpriseStep === 4 && !enterpriseData.agreed}>
                    {enterpriseStep < 4 ? 'Next' : 'Request My Quote'}
                  </button>
                </div>
              )}
            </form>
          </div>
        </div>
      )}
      <section className="faq-section">
        <h2>Frequently Asked Questions</h2>
        {faqSections.map((section, sIdx) => (
          <div key={section.title} className="faq-section-group">
            <h3>{section.title}</h3>
            {section.items.map((faq, fIdx) => {
              const key = `${sIdx}-${fIdx}`;
              const open = openFaqKey === key;
              return (
                <div key={key} className="faq-item">
                  <button
                    className="faq-question"
                    aria-expanded={open}
                    onClick={() => setOpenFaqKey(open ? null : key)}
                  >
                    {faq.q}
                  </button>
                  <div className={`faq-answer ${open ? 'open' : ''}`}>
                    <p>{faq.a}</p>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </section>
    </div>
  );
}
